import React, {
  createContext, useCallback, useContext, useEffect, useState,
} from 'react';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

export type BgSlot = 'auth' | 'home' | 'workspace';

export interface BgEntry {
  data_url: string;
  mime: string;
  size?: number;
  updated_at?: string;
}

interface PreferencesShape {
  backgrounds: Partial<Record<BgSlot, BgEntry>>;
  updated_at?: string | null;
}

interface Ctx {
  prefs: PreferencesShape;
  loading: boolean;
  reload: () => Promise<void>;
  uploadBackground: (slot: BgSlot, file: File) => Promise<void>;
  removeBackground: (slot: BgSlot) => Promise<void>;
  bgFor: (slot: BgSlot) => string | undefined;
}

const PreferencesContext = createContext<Ctx | null>(null);

/**
 * PreferencesProvider — loads user preferences (custom backgrounds) on
 * login and exposes upload/remove helpers. Preferences persist across
 * sessions because they live in the `user_preferences` collection.
 */
export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [prefs, setPrefs] = useState<PreferencesShape>({ backgrounds: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setPrefs({ backgrounds: {} });
      setLoading(false);
      return;
    }
    try {
      const data = await apiService.getPreferences();
      setPrefs({
        backgrounds: (data?.backgrounds || {}) as any,
        updated_at: data?.updated_at,
      });
    } catch {
      setPrefs({ backgrounds: {} });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    setLoading(true);
    load();
  }, [authLoading, load]);

  const uploadBackground = useCallback(async (slot: BgSlot, file: File) => {
    await apiService.uploadBackground(slot, file);
    await load();
  }, [load]);

  const removeBackground = useCallback(async (slot: BgSlot) => {
    await apiService.deleteBackground(slot);
    await load();
  }, [load]);

  const bgFor = useCallback((slot: BgSlot): string | undefined => {
    const entry = prefs.backgrounds?.[slot];
    return entry?.data_url;
  }, [prefs]);

  return (
    <PreferencesContext.Provider value={{
      prefs, loading, reload: load,
      uploadBackground, removeBackground, bgFor,
    }}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be inside PreferencesProvider');
  return ctx;
};
